import {Badge} from '@mantine/core';
import {Text} from 'src/design/atoms/Text';
import type {MRT_ColumnDef} from 'mantine-react-table';
import {useMemo} from 'react';
import type {AssistantInstanceUsageResponse} from 'src/api/ProactApiClient';
import {
  budgetColor,
  formatPercent,
  formatTokens,
  formatUsd,
} from 'src/app/assistant/ControlCenter/assistantUsageFormat';
import {useTable, useTableState} from 'src/design/organisms/useTable';

export interface AssistantInstanceUsageTableProps {
  data: AssistantInstanceUsageResponse[];
  isLoading: boolean;
  isError: boolean;
}

export const AssistantInstanceUsageTable = ({
  data,
  isLoading,
  isError,
}: AssistantInstanceUsageTableProps) => {
  const columns = useMemo<MRT_ColumnDef<AssistantInstanceUsageResponse>[]>(
    () => [
      {
        accessorKey: 'instanceName',
        header: 'Instance',
        Cell: ({row}) => (
          <Text size="sm" fw={500}>
            {row.original.instanceName ?? `Instance ${row.original.instanceId}`}
          </Text>
        ),
      },
      {
        accessorKey: 'assistantEnabled',
        header: 'Scout',
        Cell: ({row}) => (
          <Badge
            size="sm"
            variant="light"
            color={row.original.assistantEnabled ? 'green' : 'gray'}>
            {row.original.assistantEnabled ? 'On' : 'Off'}
          </Badge>
        ),
      },
      {
        id: 'tokens30d',
        header: 'Tokens (30d)',
        accessorFn: row => row.last30Days?.totalTokens ?? 0,
        Cell: ({row}) =>
          formatTokens(row.original.last30Days?.totalTokens ?? undefined),
      },
      {
        id: 'costToday',
        header: 'Cost Today',
        accessorFn: row => row.today?.estimatedCostUsd ?? 0,
        Cell: ({row}) =>
          formatUsd(row.original.today?.estimatedCostUsd ?? undefined),
      },
      {
        id: 'cost30d',
        header: 'Cost (30d)',
        accessorFn: row => row.last30Days?.estimatedCostUsd ?? 0,
        Cell: ({row}) =>
          formatUsd(row.original.last30Days?.estimatedCostUsd ?? undefined),
      },
      {
        accessorKey: 'last30ConversationCount',
        header: 'Conversations',
      },
      {
        accessorKey: 'last30ActiveUsers',
        header: 'Active Users',
      },
      {
        accessorKey: 'todayInstanceBudgetPercent',
        header: 'Budget Today',
        Cell: ({row}) => (
          <Text
            size="sm"
            c={budgetColor(row.original.todayInstanceBudgetPercent)}>
            {formatPercent(row.original.todayInstanceBudgetPercent)}
          </Text>
        ),
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'assistant-instance-usage',
    columns,
    defaultDensity: 'xs',
    defaultSorting: [{id: 'cost30d', desc: true}],
    defaultPagination: {
      pageIndex: 0,
      pageSize: 25,
    },
  });

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      enableColumnFilters: false,
      enableRowActions: false,
      columns,
      data,
      getRowId: row => String(row.instanceId),
    },
    totalCount: data.length,
    isError,
    isLoading,
    isFetching: false,
  });

  return table;
};
